// src/services/api.js
// Central API client. All components import from here — never use fetch/axios directly.
// Automatically attaches the JWT token to every request.

import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const api = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

// ---------------------------------------------------------------------------
// Request interceptor — attach JWT token from localStorage on every request
// ---------------------------------------------------------------------------
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('whl_token');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// ---------------------------------------------------------------------------
// Response interceptor — handle 401 globally (expired/invalid token)
//
// A 401 means different things depending on the request:
//   - No token attached (e.g. the login attempt itself, wrong credentials):
//     there's no session to clear — let the error propagate so the calling
//     component shows it inline instead of forcing a full page reload.
//   - A token was attached but the endpoint still returns 401 for a business
//     reason unrelated to token validity (e.g. change-password rejecting a
//     wrong current password — the `authenticate` middleware already
//     accepted the token before the route handler even ran). Redirecting
//     here would wrongly log out an already-valid session over a typo.
//   - A token was attached and got rejected by anything else — this is a
//     real expired/invalid session, so we clear it and bounce to /login.
// ---------------------------------------------------------------------------
const AUTH_401_EXEMPT_PATHS = ['/auth/login', '/auth/login-admin', '/auth/change-password'];

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const hadToken = !!error.config?.headers?.Authorization;
    const isExempt = AUTH_401_EXEMPT_PATHS.some(path => error.config?.url?.includes(path));
    if (error.response?.status === 401 && hadToken && !isExempt) {
      // Clear local session and redirect to login
      localStorage.removeItem('whl_token');
      localStorage.removeItem('whl_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ---------------------------------------------------------------------------
// Auth endpoints
// ---------------------------------------------------------------------------
export const authApi = {
  login:  (credentials) => api.post('/auth/login', credentials),
  logout: ()            => api.post('/auth/logout'),
  me:     ()            => api.get('/auth/me'),
  texts:           ()       => api.get('/auth/texts'),
  textsPublic:     (lang)  => api.get('/auth/texts/public', { params: { lang } }),
  recoverPassword: (login) => api.post('/auth/recover-password', { login }),
  kacLogin:        (kac)   => api.get('/auth/kac-login', { params: { kac } }),
  contact:         (data)  => api.post('/auth/contact', data),
  status:          ()      => api.get('/auth/status'),
  loginAdmin:      (creds) => api.post('/auth/login-admin', creds),
  setMaintenance:  (active, message) => api.post('/auth/maintenance', { active, message }),
};

// ---------------------------------------------------------------------------
// Document endpoints (to be expanded)
// ---------------------------------------------------------------------------
export const documentsApi = {
  list:      (params) => api.get('/documents', { params }),
  summary:   (params) => api.get('/documents/summary', { params }),
  canCreate:        ()         => api.get('/documents/can-create'),
  advSearchOptions: (params)   => api.get('/documents/advsearch-options', { params }),
  get:       (id)     => api.get(`/documents/${id}`),
  create:    (data)   => api.post('/documents', data),
  update:    (id, data) => api.put(`/documents/${id}`, data),
  delete:    (id)     => api.delete(`/documents/${id}`),
  docTypes:  ()       => api.get('/documents/doc-types'),
  areas:     ()       => api.get('/documents/areas'),
  privacies: ()       => api.get('/documents/privacies'),
};

// ---------------------------------------------------------------------------
// Document detail endpoints (single document — design.asp)
// ---------------------------------------------------------------------------
export const documentApi = {
  get:    (id)     => api.get(`/document/${id}`),
  getNew: (params) => api.get('/document/new', { params }),
  create: (data)   => api.post('/document', data),
  update: (id, data) => api.put(`/document/${id}`, data),
  delete:  (id)          => api.delete(`/document/${id}`),
  signoff:  (id, fieldPredef) => api.post(`/document/${id}/signoff`, { fieldPredef }),
  runQuery: (id, fieldId)    => api.post(`/document/${id}/run-query/${fieldId}`),
  notify:    (id)             => api.post(`/document/${id}/notify`),
  magicLink: (id, userId, days) => api.post(`/documents/${id}/magic-link`, { userId, days }),
};

// ---------------------------------------------------------------------------
// Tabs endpoints (files, blog, descriptors)
// ---------------------------------------------------------------------------
export const tabsApi = {
  getFiles:          (docId)          => api.get(`/tabs/${docId}/files`),
  uploadFile:        (docId, form)    => api.post(`/tabs/${docId}/files`, form, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteFile:        (docId, arcId)   => api.delete(`/tabs/${docId}/files/${arcId}`),
  updateFileDesc:    (docId, arcId, desc) => api.put(`/tabs/${docId}/files/${arcId}`, { desc }),
  getFileVersions:   (docId, arcId)      => api.get(`/tabs/${docId}/files/${arcId}/versions`),
  getBlog:           (docId)          => api.get(`/tabs/${docId}/blog`),
  postBlog:          (docId, text)    => api.post(`/tabs/${docId}/blog`, { text }),
  getDescriptors:    (docId)          => api.get(`/tabs/${docId}/descriptors`),
  searchDescriptors:  (q)             => api.get('/tabs/descriptors/search', { params: { q } }),
  searchMentions:     (q)             => api.get('/tabs/mentions/search',    { params: { q } }),
  mentionsCount:      ()              => api.get('/tabs/mentions/count'),
  mentionsUnread:     ()              => api.get('/tabs/mentions/unread'),
  markMentionsRead:   (docId)         => api.post(`/tabs/${docId}/mentions/read`),
  unreadMentions:     (docId)         => api.get(`/tabs/${docId}/mentions/unread`),
  addDescriptor:     (docId, desId)   => api.post(`/tabs/${docId}/descriptors`, { desId }),
  removeDescriptor:  (docId, desId)   => api.delete(`/tabs/${docId}/descriptors/${desId}`),
};

// ---------------------------------------------------------------------------
// Receipts endpoints
// ---------------------------------------------------------------------------
export const notificationsApi = {
  list:     ()                           => api.get('/notifications'),
  docTypes: ()                           => api.get('/notifications/doc-types'),
  save:     (tdoId, data)               => api.put(`/notifications/${tdoId}`, data),
  remove:   (tdoId, tipo, usrGrp)       => api.delete(`/notifications/${tdoId}`, { data: { tipo, usrGrp } }),
};

export const subscriptionsApi = {
  list:     ()             => api.get('/subscriptions'),
  docTypes: ()             => api.get('/subscriptions/doc-types'),
  save:     (tdoId, data)  => api.put(`/subscriptions/${tdoId}`, data),
  remove:   (tdoId)        => api.delete(`/subscriptions/${tdoId}`),
};

export const receiptsApi = {
  lotes:   ()       => api.get('/receipts/lotes'),
  results: (loteId) => api.get(`/receipts/results/${loteId}`),
  process: (loteId, supplFile) => {
    if (supplFile) {
      const fd = new FormData();
      fd.append('supplFile', supplFile);
      return api.post(`/receipts/process/${loteId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    }
    return api.post(`/receipts/process/${loteId}`);
  },
  status:  (jobId)  => api.get(`/receipts/status/${jobId}`),
  publish: (loteId) => api.post(`/receipts/publish/${loteId}`),
  deleteLote:  (loteId)        => api.delete(`/receipts/delete/${loteId}`),
};

// ---------------------------------------------------------------------------
// Users endpoints
// ---------------------------------------------------------------------------
export const usersApi = {
  list:        (search)  => api.get('/users', { params: { search } }),
  groups:      ()        => api.get('/users/groups'),
  profiles:    ()        => api.get('/users/profiles'),
  create:      (data)    => api.post('/users', data),
  update:      (id, data)=> api.put(`/users/${id}`, data),
  toggle:      (id)      => api.put(`/users/${id}/toggle`),
  impersonate: (id)      => api.post(`/users/${id}/impersonate`),
  welcomeMail: (id)      => api.post(`/users/${id}/welcome-mail`),
  magicLink:   (id, days)=> api.post(`/users/${id}/magic-link`, { days }),
};

// ---------------------------------------------------------------------------
// Filters endpoints
// ---------------------------------------------------------------------------
export const filtersApi = {
  menu: () => api.get('/filters/menu'),
};

// ---------------------------------------------------------------------------
// Field endpoints
// ---------------------------------------------------------------------------
export const fieldsApi = {
  getByDocType: (docTypeId) => api.get(`/fields/by-type/${docTypeId}`),
  runQuery:     (fieldId, docId) => api.post(`/fields/${fieldId}/run-query`, { docId }),
};

// ---------------------------------------------------------------------------
// File endpoints
// ---------------------------------------------------------------------------
export const filesApi = {
  list:   (docId)      => api.get(`/files`, { params: { docId } }),
  upload: (docId, formData) => api.post(`/files/upload/${docId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  delete: (fileId)     => api.delete(`/files/${fileId}`),
};

// ---------------------------------------------------------------------------
// Setup / parametrization endpoints
// ---------------------------------------------------------------------------
export const setupApi = {
  // Filtros
  empresas:             ()        => api.get('/setup/empresas'),
  filtros:              ()        => api.get('/setup/filtros'),
  createFiltro:         (data)    => api.post('/setup/filtros', data),
  updateFiltro:         (id, data)=> api.put(`/setup/filtros/${id}`, data),
  deleteFiltro:         (id)      => api.delete(`/setup/filtros/${id}`),
  grupos:               ()        => api.get('/setup/grupos'),
  createGrupo:          (data)    => api.post('/setup/grupos', data),
  updateGrupo:          (id, data)=> api.put(`/setup/grupos/${id}`, data),
  deleteGrupo:          (id)      => api.delete(`/setup/grupos/${id}`),
  filgrupo:             ()        => api.get('/setup/filgrupo'),
  filgrupoOptions:      ()        => api.get('/setup/filgrupo/options'),
  createFilgrupo:       (data)    => api.post('/setup/filgrupo', data),
  deleteFilgrupo:       (filId, grpId) => api.delete(`/setup/filgrupo?filId=${filId}&grpId=${grpId}`),
  // Temas
  temas:                ()        => api.get('/setup/temas'),
  createTema:           (data)    => api.post('/setup/temas', data),
  updateTema:           (id, data)=> api.put(`/setup/temas/${id}`, data),
  deleteTema:           (id)      => api.delete(`/setup/temas/${id}`),
  // Filtros por tema
  filtema:              ()        => api.get('/setup/filtema'),
  createFiltema:        (data)    => api.post('/setup/filtema', data),
  deleteFiltema:        (temId, filId) => api.delete(`/setup/filtema?temId=${temId}&filId=${filId}`),
  // Tipos de campo
  tiposCampo:           ()        => api.get('/setup/tiposcampo'),
  // Vistas (templates + por tipodoc)
  vistemplates:         ()              => api.get('/setup/vistemplates'),
  createVistemplate:    (data)          => api.post('/setup/vistemplates', data),
  updateVistemplate:    (id, data)      => api.put(`/setup/vistemplates/${id}`, data),
  deleteVistemplate:    (id)            => api.delete(`/setup/vistemplates/${id}`),
  vistas:               (tdocId)        => api.get(`/setup/vistas/${tdocId}`),
  saveVistas:           (tdocId, data)  => api.put(`/setup/vistas/${tdocId}`, data),
  // Tipos de documentos
  tipodocs:             ()        => api.get('/setup/tipodocs'),
  createTipodoc:        (data)    => api.post('/setup/tipodocs', data),
  updateTipodoc:        (id, data)=> api.put(`/setup/tipodocs/${id}`, data),
  deleteTipodoc:        (id)      => api.delete(`/setup/tipodocs/${id}`),
  // Campos
  campos:               ()        => api.get('/setup/campos'),
  createCampo:          (data)    => api.post('/setup/campos', data),
  updateCampo:          (id, data)=> api.put(`/setup/campos/${id}`, data),
  deleteCampo:          (id)      => api.delete(`/setup/campos/${id}`),
  // Posibilidades
  posibilidades:        ()        => api.get('/setup/posibilidades'),
  createPosibilidad:    (data)    => api.post('/setup/posibilidades', data),
  updatePosibilidad:    (id, data)=> api.put(`/setup/posibilidades/${id}`, data),
  deletePosibilidad:    (id)      => api.delete(`/setup/posibilidades/${id}`),
  // Secciones
  secciones:            ()        => api.get('/setup/secciones'),
  createSeccion:        (data)    => api.post('/setup/secciones', data),
  updateSeccion:        (id, data)=> api.put(`/setup/secciones/${id}`, data),
  deleteSeccion:        (id)      => api.delete(`/setup/secciones/${id}`),
  // Áreas
  areas:                ()        => api.get('/setup/areas'),
  createArea:           (data)    => api.post('/setup/areas', data),
  updateArea:           (id, data)=> api.put(`/setup/areas/${id}`, data),
  deleteArea:           (id)      => api.delete(`/setup/areas/${id}`),
  // Admin info + logs
  adminInfo:            ()          => api.get('/setup/admin-info'),
  logAccesos:           (usrId)     => api.get('/setup/log-accesos', { params: usrId ? { usrId } : {} }),
  logAccesosSesion:     (usrId, fecha) => api.get('/setup/log-accesos/sesion', { params: { usrId, fecha } }),
  logBlog:              (usrId)     => api.get('/setup/log-blog',    { params: usrId ? { usrId } : {} }),
  // Empresas CRUD
  empresasCrud:         ()        => api.get('/setup/empresas-crud'),
  createEmpresa:        (data)    => api.post('/setup/empresas-crud', data),
  updateEmpresa:        (id,data) => api.put(`/setup/empresas-crud/${id}`, data),
  deleteEmpresa:        (id)      => api.delete(`/setup/empresas-crud/${id}`),
  // Parámetros
  parametros:           ()        => api.get('/setup/parametros'),
  createParametro:      (data)    => api.post('/setup/parametros', data),
  updateParametro:      (nom,data)=> api.put(`/setup/parametros/${encodeURIComponent(nom)}`, data),
  deleteParametro:      (nom)     => api.delete(`/setup/parametros/${encodeURIComponent(nom)}`),
  // Notificaciones admin
  notifAdmin:           ()                      => api.get('/setup/notif-admin'),
  saveNotifAdmin:       (usrId,tdoId,data)      => api.put(`/setup/notif-admin/${usrId}/${tdoId}`, data),
  deleteNotifAdmin:     (usrId,tdoId,data)      => api.delete(`/setup/notif-admin/${usrId}/${tdoId}`, { data }),
  // Tesauro
  tesauro:              ()        => api.get('/setup/tesauro'),
  createTesauro:        (data)    => api.post('/setup/tesauro', data),
  updateTesauro:        (id,data) => api.put(`/setup/tesauro/${id}`, data),
  deleteTesauro:        (id)      => api.delete(`/setup/tesauro/${id}`),
  // Descriptores
  descriptores:         ()        => api.get('/setup/descriptores'),
  createDescriptor:     (data)    => api.post('/setup/descriptores', data),
  updateDescriptor:     (id,data) => api.put(`/setup/descriptores/${id}`, data),
  deleteDescriptor:     (id)      => api.delete(`/setup/descriptores/${id}`),
  // Descriptores por tipo doc
  doctes:               ()        => api.get('/setup/doctes'),
  createDocte:          (data)    => api.post('/setup/doctes', data),
  deleteDocte:          (tdo,des) => api.delete(`/setup/doctes/${tdo}/${des}`),
  // Autorizaciones directas
  autoriz:              ()                       => api.get('/setup/autoriz'),
  autorizGrupos:        ()                       => api.get('/setup/autoriz/grupos'),
  createAutoriz:        (data)                   => api.post('/setup/autoriz', data),
  updateAutoriz:        (grp,tdo,ger,pri, data)  => api.put(`/setup/autoriz/${grp}/${tdo}/${ger}/${pri}`, data),
  deleteAutoriz:        (grp,tdo,ger,pri)        => api.delete(`/setup/autoriz/${grp}/${tdo}/${ger}/${pri}`),
  // Autorizaciones relacionadas
  autrelac:             ()                       => api.get('/setup/autrelac'),
  autrelacGrupos:       ()                       => api.get('/setup/autrelac/grupos'),
  createAutrelac:       (data)                   => api.post('/setup/autrelac', data),
  updateAutrelac:       (grp,tdo,ger,pri, data)  => api.put(`/setup/autrelac/${grp}/${tdo}/${ger}/${pri}`, data),
  deleteAutrelac:       (grp,tdo,ger,pri)        => api.delete(`/setup/autrelac/${grp}/${tdo}/${ger}/${pri}`),
  // Usuarios por perfil
  usrper:               ()             => api.get('/setup/usrper'),
  usrperUsuarios:       ()             => api.get('/setup/usrper/usuarios'),
  createUsrper:         (data)         => api.post('/setup/usrper', data),
  deleteUsrper:         (usrId, perId) => api.delete(`/setup/usrper/${usrId}/${perId}`),
  // Funciones por perfil
  perffun:              ()               => api.get('/setup/perffun'),
  createPerffun:        (data)           => api.post('/setup/perffun', data),
  deletePerffun:        (perId, funId)   => api.delete(`/setup/perffun/${perId}/${funId}`),
  // Perfiles
  perfiles:             ()        => api.get('/setup/perfiles'),
  createPerfil:         (data)    => api.post('/setup/perfiles', data),
  updatePerfil:         (id, data)=> api.put(`/setup/perfiles/${id}`, data),
  deletePerfil:         (id)      => api.delete(`/setup/perfiles/${id}`),
  // Funciones
  sqlExec:              (query)   => api.post('/setup/sql-exec', { query }),
  funciones:            ()        => api.get('/setup/funciones'),
  createFuncion:        (data)    => api.post('/setup/funciones', data),
  updateFuncion:        (id, data)=> api.put(`/setup/funciones/${id}`, data),
  deleteFuncion:        (id)      => api.delete(`/setup/funciones/${id}`),
  // Privacidad
  privacidad:           ()        => api.get('/setup/privacidad'),
  createPrivacidad:     (data)    => api.post('/setup/privacidad', data),
  updatePrivacidad:     (id, data)=> api.put(`/setup/privacidad/${id}`, data),
  deletePrivacidad:     (id)      => api.delete(`/setup/privacidad/${id}`),
  // Diseño de fichas
  disenio:              (tdocId, tipDis) => api.get('/setup/disenio', { params: { tdocId, tipDis } }),
  disenioDisponibles:   (tdocId, tipDis) => api.get('/setup/disenio/campos', { params: { tdocId, tipDis } }),
  disenioPreview:       (tdocId, tipDis) => api.get('/setup/disenio/preview', { params: { tdocId, tipDis } }),
  saveDisenio:          (data)           => api.put('/setup/disenio', data),
  deleteDisenio:        (tdocId, tipDis, campoId) => api.delete(`/setup/disenio/${tdocId}/${tipDis}/${campoId}`),
};

export const firmaApi = {
  iniciar:  (arcId)        => api.post(`/firma/iniciar/${arcId}`),
  estado:   (arcId)        => api.get(`/firma/estado/${arcId}`),
  info:     (arcId)        => api.get(`/firma/info/${arcId}`),
};

export default api;
